// Reusable signature surface — used by the Install Agreement (SigningPage.tsx)
// and the Color Confirmation Addendum (ColorSelection.tsx).
//
// Two ways to sign:
//   - Draw  → finger or mouse, classic ink-style pad
//   - Type  → homeowner types their name, rendered onto the canvas in Caveat
//             script so the downstream PDF + storage path don't care which
//             mode was used (everything is still a PNG dataUrl).
//
// Two surface sizes:
//   - Inline → the embedded ~128px-tall pad inside the signing form
//   - Full-screen → fixed-overlay modal that fills the viewport, so the
//                   homeowner can rotate to landscape and use the whole screen
//
// May 11, 2026 (initial): fix "page scrolls while signing" bug.
//   Root cause: React's synthetic onTouchMove is registered as a PASSIVE
//   listener; calling e.preventDefault() inside it is a no-op, so iOS
//   Safari and Android Chrome treat the finger drag as a page scroll.
//   Fix: touch-action:none on canvas + container, native non-passive
//   touch listeners, overscroll-behavior:contain.
//
// May 11, 2026 (later): added Type-to-sign mode + full-screen modal at
//   Greg's request — finger signing on a 128px pad is too small for many
//   homeowners, and not everyone wants to draw with their finger at all.

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Caveat preload — script font used for typed signatures. The face is loaded
// site-wide via the @import at the top of index.css, but canvas rendering
// won't pick up a webfont until the browser has actually parsed and cached
// the glyphs. We poke document.fonts.load() at module init so the first
// draw inside Type mode renders in Caveat instead of falling through to
// "Bradley Hand" / "Segoe Script" / generic cursive.
// ---------------------------------------------------------------------------

if (typeof document !== "undefined" && "fonts" in document) {
  // Fire-and-forget — failures fall back to the OS cursive face, which is
  // still recognizably script and never blocks signing.
  void document.fonts.load('48px "Caveat"').catch(() => {});
  void document.fonts.load('700 48px "Caveat"').catch(() => {});
}

// Public API. Kept stable so SigningPage + ColorSelection don't need updates.
type SignatureCanvasProps = {
  onSign: (dataUrl: string) => void;
  onClear: () => void;
  /** Inline pad height in px. Defaults to 128. */
  height?: number;
  /** Pre-fills the Type-mode input so the homeowner doesn't have to retype
   *  their name. Falls back to empty string. */
  defaultTypedName?: string;
};

type SignMode = "draw" | "type";

// ---------------------------------------------------------------------------
// Main exported component
// ---------------------------------------------------------------------------

export default function SignatureCanvas({
  onSign,
  onClear,
  height = 128,
  defaultTypedName = "",
}: SignatureCanvasProps) {
  const [mode, setMode] = useState<SignMode>("draw");
  const [typedName, setTypedName] = useState<string>(defaultTypedName);
  const [hasInlineSignature, setHasInlineSignature] = useState(false);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  // When the user signs in the full-screen overlay and the inline pad isn't
  // mounted yet (e.g. they previously had Type mode active), we store the
  // dataUrl here and a useEffect below applies it once DrawCanvas commits.
  // Without this defer, inlineDrawRef.current is null at onDone-time so the
  // image never lands on the small pad — the signature still submits via
  // onSign, but the inline canvas would look blank, which reads as a bug.
  const [pendingDrawLoad, setPendingDrawLoad] = useState<string | null>(null);

  // The draw-canvas controller exposes imperative ops so we can clear/reset
  // both the inline pad and the full-screen pad without messy ref juggling.
  const inlineDrawRef = useRef<DrawCanvasHandle>(null);

  // Apply a pending overlay-drawn signature once DrawCanvas has mounted +
  // committed its ref. By the time this effect runs, React has finished the
  // commit phase so the ref is populated.
  useEffect(() => {
    if (mode === "draw" && pendingDrawLoad && inlineDrawRef.current) {
      inlineDrawRef.current.loadFromDataUrl(pendingDrawLoad);
      setPendingDrawLoad(null);
    }
  }, [mode, pendingDrawLoad]);

  // When the user typed a name, render it onto an off-screen canvas + emit
  // the dataUrl. Re-renders any time the typed name changes so the homeowner
  // sees the preview update live.
  useEffect(() => {
    if (mode !== "type") return;
    if (!typedName.trim()) {
      // Empty typed name = no signature yet
      setHasInlineSignature(false);
      onClear();
      return;
    }
    const dataUrl = renderTypedSignatureToPng(typedName);
    if (dataUrl) {
      setHasInlineSignature(true);
      onSign(dataUrl);
    }
  }, [mode, typedName, onSign, onClear]);

  // Switching modes clears the prior signature so the homeowner doesn't
  // accidentally submit with a stale Draw value when they meant to Type
  // (or vice versa).
  const handleModeSwitch = (next: SignMode) => {
    if (next === mode) return;
    setMode(next);
    setHasInlineSignature(false);
    inlineDrawRef.current?.clear();
    if (next === "draw") {
      // Drop the typed-render dataUrl from the parent — fresh start.
      onClear();
    }
    // For switch INTO type mode, the useEffect above will re-emit the
    // current typedName (if any) which keeps state coherent.
  };

  const handleClearAll = () => {
    // Empty the typed input on Clear — resetting to defaultTypedName would
    // silently rewrite the homeowner's name back, which conflicts with the
    // user's intent ("start over").
    setTypedName("");
    setHasInlineSignature(false);
    inlineDrawRef.current?.clear();
    onClear();
  };

  return (
    <div className="space-y-2">
      {/* Mode tabs + Expand button */}
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium">
          <button
            type="button"
            onClick={() => handleModeSwitch("draw")}
            className={`px-3 py-1.5 rounded-md transition ${
              mode === "draw"
                ? "bg-white text-bardin-ink shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            ✏️ Draw
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch("type")}
            className={`px-3 py-1.5 rounded-md transition ${
              mode === "type"
                ? "bg-white text-bardin-ink shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            ⌨️ Type
          </button>
        </div>
        <button
          type="button"
          onClick={() => setFullScreenOpen(true)}
          className="text-xs font-medium text-bardin-teal hover:text-bardin-teal/80 inline-flex items-center gap-1"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7V3h4M21 7V3h-4M3 17v4h4M21 17v4h-4" />
          </svg>
          Sign full-screen
        </button>
      </div>

      {/* Inline surface — Draw or Type */}
      {mode === "draw" ? (
        <DrawCanvas
          ref={inlineDrawRef}
          height={height}
          onSign={(d) => {
            setHasInlineSignature(true);
            onSign(d);
          }}
          onClear={() => {
            setHasInlineSignature(false);
            onClear();
          }}
        />
      ) : (
        <TypeSurface
          typedName={typedName}
          onTypedNameChange={setTypedName}
          height={height}
        />
      )}

      {/* Clear button — only when something has been signed */}
      {hasInlineSignature && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Clear signature
          </button>
        </div>
      )}

      {/* Full-screen overlay */}
      {fullScreenOpen && (
        <FullScreenSignatureOverlay
          initialMode={mode}
          initialTypedName={typedName}
          onCancel={() => setFullScreenOpen(false)}
          onDone={(result) => {
            // Sync the inline state with what was captured in the overlay
            // so the homeowner sees a consistent signature when they close it.
            if (result.mode === "type") {
              setMode("type");
              setTypedName(result.typedName);
              // The typedName useEffect will re-render onto the inline
              // canvas + emit onSign.
            } else {
              setMode("draw");
              setHasInlineSignature(true);
              onSign(result.dataUrl);
              // Defer the inline-canvas load via state — if the previous
              // inline mode was "type", DrawCanvas isn't mounted yet and
              // inlineDrawRef.current is null at this point. The pending-load
              // useEffect above picks it up once the canvas commits.
              setPendingDrawLoad(result.dataUrl);
            }
            setFullScreenOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DrawCanvas — the actual ink-style finger/mouse pad. The May 11 scroll-fix
// pattern lives here (touch-action:none + native non-passive listeners).
// ---------------------------------------------------------------------------

type DrawCanvasHandle = {
  clear: () => void;
  loadFromDataUrl: (dataUrl: string) => void;
  toDataUrl: () => string | null;
};

const DrawCanvas = forwardRef<
  DrawCanvasHandle,
  {
    height: number;
    onSign: (dataUrl: string) => void;
    onClear: () => void;
    /** When true, render the placeholder hint in the lower half so it
     *  doesn't sit under the user's hand on a tall full-screen pad. */
    placeholderLow?: boolean;
  }
>(function DrawCanvas({ height, onSign, onClear, placeholderLow }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const hasSignedRef = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    // Preserve any existing strokes across resize (e.g. orientation change
    // while in full-screen mode).
    const before = canvas.width > 0 && hasSignedRef.current
      ? canvas.toDataURL("image/png")
      : null;
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.strokeStyle = "#2a2e30";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
    if (before) {
      const img = new Image();
      img.onload = () => {
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = before;
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("orientationchange", resizeCanvas);
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("orientationchange", resizeCanvas);
    };
  }, [resizeCanvas]);

  // Native touch listeners — passive: false so preventDefault() actually
  // stops iOS/Android from scrolling the page mid-signature.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const posFromTouch = (t: Touch) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (t.clientX - rect.left) * (canvas.width / rect.width),
        y: (t.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const handleStart = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 0) return;
      drawingRef.current = true;
      const p = posFromTouch(e.touches[0]);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
      }
    };

    const handleMove = (e: TouchEvent) => {
      e.preventDefault();
      if (!drawingRef.current || e.touches.length === 0) return;
      const p = posFromTouch(e.touches[0]);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      if (!hasSignedRef.current) {
        hasSignedRef.current = true;
        setHasSigned(true);
      }
    };

    const handleEnd = (e: TouchEvent) => {
      e.preventDefault();
      drawingRef.current = false;
      if (hasSignedRef.current) {
        onSign(canvas.toDataURL("image/png"));
      }
    };

    const handleCancel = (e: TouchEvent) => {
      e.preventDefault();
      drawingRef.current = false;
    };

    canvas.addEventListener("touchstart", handleStart, { passive: false });
    canvas.addEventListener("touchmove", handleMove, { passive: false });
    canvas.addEventListener("touchend", handleEnd, { passive: false });
    canvas.addEventListener("touchcancel", handleCancel, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", handleStart);
      canvas.removeEventListener("touchmove", handleMove);
      canvas.removeEventListener("touchend", handleEnd);
      canvas.removeEventListener("touchcancel", handleCancel);
    };
  }, [onSign]);

  // Mouse handlers — React synthetic is fine, no passive-listener issue.
  const getMousePos = (e: React.MouseEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawMouse = (e: React.MouseEvent) => {
    e.preventDefault();
    drawingRef.current = true;
    const pos = getMousePos(e);
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  };

  const drawMouse = (e: React.MouseEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getMousePos(e);
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    if (!hasSignedRef.current) {
      hasSignedRef.current = true;
      setHasSigned(true);
    }
  };

  const endDrawMouse = () => {
    drawingRef.current = false;
    if (hasSignedRef.current && canvasRef.current) {
      onSign(canvasRef.current.toDataURL("image/png"));
    }
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      hasSignedRef.current = false;
      setHasSigned(false);
      onClear();
    },
    loadFromDataUrl: (dataUrl: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        hasSignedRef.current = true;
        setHasSigned(true);
      };
      img.src = dataUrl;
    },
    toDataUrl: () => {
      const canvas = canvasRef.current;
      if (!canvas || !hasSignedRef.current) return null;
      return canvas.toDataURL("image/png");
    },
  }), [onClear]);

  return (
    <div
      ref={containerRef}
      className="relative bg-white border-2 border-dashed border-slate-300 rounded-xl overflow-hidden cursor-crosshair"
      style={{
        height,
        // touch-action:none = the browser won't interpret touches that start
        // here as scroll/zoom gestures.
        touchAction: "none",
        overscrollBehavior: "contain",
      }}
    >
      {!hasSigned && (
        <div
          className={`absolute inset-x-0 flex items-center justify-center text-slate-400 text-sm pointer-events-none ${
            placeholderLow ? "bottom-6" : "inset-y-0"
          }`}
        >
          Tap or click to sign
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ touchAction: "none" }}
        onMouseDown={startDrawMouse}
        onMouseMove={drawMouse}
        onMouseUp={endDrawMouse}
        onMouseLeave={endDrawMouse}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// TypeSurface — input field + live preview rendered in Caveat script font.
// The rendering-to-PNG happens in the parent useEffect via
// renderTypedSignatureToPng() so it stays in sync between inline + overlay.
// ---------------------------------------------------------------------------

function TypeSurface({
  typedName,
  onTypedNameChange,
  height,
}: {
  typedName: string;
  onTypedNameChange: (s: string) => void;
  height: number;
}) {
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={typedName}
        onChange={(e) => onTypedNameChange(e.target.value)}
        placeholder="Type your full name"
        autoComplete="name"
        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
      />
      <div
        className="relative bg-white border-2 border-dashed border-slate-300 rounded-xl overflow-hidden flex items-center justify-center px-4"
        style={{ height }}
      >
        {typedName.trim() ? (
          <span
            className="text-bardin-ink select-none truncate max-w-full"
            style={{
              fontFamily: '"Caveat", "Bradley Hand", "Segoe Script", cursive',
              fontSize: Math.max(28, Math.min(64, height * 0.6)),
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {typedName}
          </span>
        ) : (
          <span className="text-slate-400 text-sm">Your signature will appear here</span>
        )}
      </div>
      <p className="text-[10px] text-slate-400 text-center leading-relaxed">
        By typing your name above, you adopt this as your electronic signature with the same legal effect as a hand-drawn signature.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// renderTypedSignatureToPng — off-screen canvas → PNG dataUrl. Caveat is
// preloaded at module init via document.fonts.load() so the first render
// uses the script face instead of the fallback.
// ---------------------------------------------------------------------------

function renderTypedSignatureToPng(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;

  // Render at a 2x backing canvas so the resulting PNG looks crisp when
  // pdf-lib draws it at the standard agreement signature box size (~250x80pt).
  const W = 800;
  const H = 200;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Transparent background — the install-agreement PDF renders the signature
  // on top of the printed signature-line, and pdf-lib's drawImage preserves
  // PNG alpha.
  ctx.clearRect(0, 0, W, H);

  // Auto-size: shrink the font if the name is too long for the canvas.
  ctx.fillStyle = "#2a2e30";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  let size = 120;
  do {
    ctx.font = `600 ${size}px "Caveat", "Bradley Hand", "Segoe Script", cursive`;
    const w = ctx.measureText(trimmed).width;
    if (w <= W - 40) break;
    size -= 6;
  } while (size > 36);

  ctx.fillText(trimmed, W / 2, H / 2);
  return canvas.toDataURL("image/png");
}

// ---------------------------------------------------------------------------
// FullScreenSignatureOverlay — fixed-inset overlay that fills the viewport.
// Same Draw/Type tabs as the inline form but with a much larger surface.
// On portrait phones we surface a "rotate for more room" hint, but the
// canvas auto-resizes via the orientationchange listener so signing in
// either orientation still works.
// ---------------------------------------------------------------------------

type OverlayDoneResult =
  | { mode: "draw"; dataUrl: string }
  | { mode: "type"; typedName: string; dataUrl: string };

function FullScreenSignatureOverlay({
  initialMode,
  initialTypedName,
  onCancel,
  onDone,
}: {
  initialMode: SignMode;
  initialTypedName: string;
  onCancel: () => void;
  onDone: (result: OverlayDoneResult) => void;
}) {
  const [mode, setMode] = useState<SignMode>(initialMode);
  const [typedName, setTypedName] = useState<string>(initialTypedName);
  const [drawnDataUrl, setDrawnDataUrl] = useState<string | null>(null);
  const [hasContent, setHasContent] = useState<boolean>(
    initialMode === "type" ? !!initialTypedName.trim() : false,
  );
  const drawRef = useRef<DrawCanvasHandle>(null);
  const [isPortrait, setIsPortrait] = useState<boolean>(
    typeof window !== "undefined" ? window.innerHeight > window.innerWidth : true,
  );

  useEffect(() => {
    const onResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Lock body scroll while overlay is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Escape closes the overlay — desktop accessibility + iPad with a keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const handleModeSwitch = (next: SignMode) => {
    if (next === mode) return;
    setMode(next);
    if (next === "draw") {
      drawRef.current?.clear();
      setDrawnDataUrl(null);
      setHasContent(false);
    } else {
      setHasContent(!!typedName.trim());
    }
  };

  const handleClear = () => {
    if (mode === "draw") {
      drawRef.current?.clear();
      setDrawnDataUrl(null);
    } else {
      setTypedName("");
    }
    setHasContent(false);
  };

  const handleDone = () => {
    if (mode === "type") {
      const dataUrl = renderTypedSignatureToPng(typedName);
      if (!dataUrl) return;
      onDone({ mode: "type", typedName, dataUrl });
    } else {
      const dataUrl = drawnDataUrl ?? drawRef.current?.toDataUrl();
      if (!dataUrl) return;
      onDone({ mode: "draw", dataUrl });
    }
  };

  // Canvas height — generous so finger signing has room. We subtract space
  // for the chrome (tabs + buttons + hint).
  const canvasHeight = Math.max(220, Math.floor(window.innerHeight * 0.55));

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/85 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Signature pad"
    >
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-medium text-slate-500 hover:text-slate-700"
        >
          Cancel
        </button>
        <div className="text-sm font-semibold text-bardin-ink">Sign Here</div>
        <button
          type="button"
          onClick={handleDone}
          disabled={!hasContent}
          className={`text-sm font-semibold ${
            hasContent ? "text-bardin-orange hover:text-bardin-orange/80" : "text-slate-300 cursor-not-allowed"
          }`}
        >
          Done
        </button>
      </div>

      {/* Mode toggle */}
      <div className="bg-white px-4 pt-3 pb-2 flex items-center justify-center">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm font-medium">
          <button
            type="button"
            onClick={() => handleModeSwitch("draw")}
            className={`px-5 py-1.5 rounded-md transition ${
              mode === "draw"
                ? "bg-white text-bardin-ink shadow-sm"
                : "text-slate-500"
            }`}
          >
            ✏️ Draw
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch("type")}
            className={`px-5 py-1.5 rounded-md transition ${
              mode === "type"
                ? "bg-white text-bardin-ink shadow-sm"
                : "text-slate-500"
            }`}
          >
            ⌨️ Type
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 bg-white px-4 pb-4 overflow-y-auto flex flex-col">
        {/* Portrait hint — only meaningful on a phone-sized portrait viewport */}
        {isPortrait && window.innerWidth < 700 && (
          <div className="mb-2 flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12" y2="18" />
            </svg>
            Tip: rotate your phone sideways for a wider signing area
          </div>
        )}

        {mode === "draw" ? (
          <>
            <DrawCanvas
              ref={drawRef}
              height={canvasHeight}
              placeholderLow
              onSign={(d) => {
                setDrawnDataUrl(d);
                setHasContent(true);
              }}
              onClear={() => {
                setDrawnDataUrl(null);
                setHasContent(false);
              }}
            />
            <div className="flex items-center justify-between mt-3">
              <div className="text-xs text-slate-500">
                Use your finger to sign above
              </div>
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Clear
              </button>
            </div>
          </>
        ) : (
          <div className="mt-2">
            <input
              type="text"
              value={typedName}
              onChange={(e) => {
                setTypedName(e.target.value);
                setHasContent(!!e.target.value.trim());
              }}
              placeholder="Type your full name"
              autoComplete="name"
              autoFocus
              className="w-full border border-slate-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
            />
            <div
              className="mt-3 relative bg-white border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center px-6"
              style={{ height: canvasHeight }}
            >
              {typedName.trim() ? (
                <span
                  className="text-bardin-ink select-none truncate max-w-full"
                  style={{
                    fontFamily: '"Caveat", "Bradley Hand", "Segoe Script", cursive',
                    fontSize: Math.max(64, Math.min(140, canvasHeight * 0.55)),
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  {typedName}
                </span>
              ) : (
                <span className="text-slate-400 text-sm">Your signature will appear here</span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 text-center leading-relaxed mt-3">
              By typing your name above, you adopt this as your electronic signature with the same legal effect as a hand-drawn signature.
            </p>
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
